import { Component, inject, ViewChild } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { DeviceDetectorService } from "ngx-device-detector";
import { EMPTY } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

import { AuthModel, Credentials, EXPLICIT_SELECT_ORGANISATION, PermissionDeniedError } from "@intocare/auth-data";
import {
	MFARequiredError,
	MustEnableMFAError,
	MustSetMainFunctionError,
	PasswordRequiredError,
	UnknownProblemError,
} from "@intocare/aws";
import { Environment, ENVIRONMENT_TOKEN } from "@intocare/environments";

import { LoginFormComponent } from "../../components/login-form/login-form.component";

@Component({
	templateUrl: "./sign-in-with-username.component.html",
	styleUrls: ["./sign-in-with-username.component.scss"],
	standalone: false,
})
export class SignInWithUsernameComponent {
	private auth = inject(AuthModel);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private detector = inject(DeviceDetectorService);

	@ViewChild(LoginFormComponent, { static: false }) loginForm: LoginFormComponent;
	loading = false;
	error$ = this.auth.errorMessage$;
	inactivity = this.auth.inactivity;
	organisation$ = this.route.paramMap.pipe(
		map((params: ParamMap) => {
			const org = params.get("org");
			const home = params.get("home");

			if (!org && !home) {
				// If no org and home was found, we did not specify an organisation in the url
				return;
			}

			if (!home) {
				// If no home was found, we are using hq
				return org;
			}

			return `${org}.${home}`;
		}),
		map((organisation) => organisation && organisation.toLowerCase()),
	);

	isInvalidBrowser = false;

	constructor() {
		const environment = inject<Environment>(ENVIRONMENT_TOKEN);

		const { browser } = this.detector;
		this.isInvalidBrowser = environment.UNSUPPORTED_BROWSERS.has(browser.toLowerCase());
	}

	onAuthenticate(credentials: Credentials) {
		this.loading = true;
		this.auth
			.authenticate(credentials)
			.pipe(
				tap(() => {
					window.localStorage.setItem("currentOrganisation", credentials.organisation);
					const { redirectTo } = this.route.snapshot.queryParams;

					const redirectToCurrentOrganisation = redirectTo
						? this.rewriteRedirectUrlWithCurrentOrganisation(redirectTo, credentials.organisation)
						: undefined;

					if (redirectToCurrentOrganisation) {
						this.router.navigateByUrl(redirectToCurrentOrganisation);
					} else {
						this.router.navigate([credentials.organisation.split(".").join("/"), "dashboard"]);
					}
				}),
				catchError((err: unknown) => {
					if (err instanceof MFARequiredError) {
						this.router.navigate([credentials.organisation.split(".").join("/"), "mfa"]);
						return EMPTY;
					}
					if (err instanceof PasswordRequiredError) {
						this.router.navigate([credentials.organisation.split(".").join("/"), "confirm"]);
						return EMPTY;
					}
					if (err instanceof PermissionDeniedError) {
						this.router.navigate([credentials.organisation.split(".").join("/"), "no-permissions"]);
						return EMPTY;
					}
					if (err instanceof MustEnableMFAError) {
						this.router.navigate([credentials.organisation.split(".").join("/"), "mfa-setup"]);
						return EMPTY;
					}
					if (err instanceof MustSetMainFunctionError) {
						this.router.navigate(["/must-set-main-function"]);
						return EMPTY;
					}
					if (err instanceof UnknownProblemError) {
						this.router.navigate(["/unknown-problem"]);
						return EMPTY;
					}

					this.clearPassword();
					this.loading = false;
					return EMPTY;
				}),
			)
			.subscribe();
	}

	/**
	 * Function that will rewrite the redirectUrl to the current organisation if the redirectUrl contains a different
	 * organisation.
	 */
	private rewriteRedirectUrlWithCurrentOrganisation(redirectUrl: string, currentOrganisation: string): string {
		const url = redirectUrl.startsWith("/") ? redirectUrl.slice(1) : redirectUrl;
		const currentOrganisationIsHq = currentOrganisation.split(".").length === 1;

		const isRedirectOrganisationSameAsCurrentOrganisation = currentOrganisationIsHq
			? this.isRedirectHqSameAsCurrentHq({ redirectUrl: url, currentHq: currentOrganisation })
			: this.isRedirectHomeSameAsCurrentHome({ redirectUrl: url, currentHome: currentOrganisation });

		if (isRedirectOrganisationSameAsCurrentOrganisation) {
			return redirectUrl;
		} else {
			return currentOrganisation.replace(".", "/");
		}
	}

	private isRedirectHqSameAsCurrentHq({ redirectUrl, currentHq }: { redirectUrl: string; currentHq: string }): boolean {
		return currentHq === redirectUrl.split("/")[0];
	}

	private isRedirectHomeSameAsCurrentHome({
		redirectUrl,
		currentHome,
	}: {
		redirectUrl: string;
		currentHome: string;
	}): boolean {
		return currentHome === `${redirectUrl.split("/")[0]}.${redirectUrl.split("/")[1]}`;
	}

	clearPassword() {
		this.loginForm.clearPassword();
	}

	selectOrganisation() {
		this.router.navigate(["select-organisation"], { state: { [EXPLICIT_SELECT_ORGANISATION]: true } });
	}
}
